import { useEffect, useMemo } from 'react';
import { SEARCH_QUERY as PUBLIC_SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { useAppliedFilters } from '../../filters/hooks/useAppliedFilters';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useSearchParams } from '../../hooks/useSearchParams';
import { SORT_OPTIONS } from '../components/SortDropdown';
import { DEFAULT_QUERY_TYPE, QUERY_TYPES, SEARCH_TIMEOUT_MS } from '../constants';
import { useCachedSearchData } from './useCachedSearchData';
import { useCollectionQuery } from './useCollectionQuery';
import { useSearchQueryTransformer } from './useSearchQueryTransformer';

/**
 * Main hook for handling search results.
 * Manages the transition between LLM and ElasticSearch queries,
 * handles caching of results, and processes search parameters.
 */
export function useSearchResultsQuery() {
  var _params$get, _collectionResult$dat, _searchResult$data2;
  const appliedFilters = useAppliedFilters();
  const params = useSearchParams();
  const collectionResult = useCollectionQuery();
  const {
    cachedListingIds,
    getProcessedSearchData,
    processSearchResultData,
    shouldUseCachedSearchData
  } = useCachedSearchData();
  const page = Number(params.get('page')) || 1;
  const queryType = (_params$get = params.get('queryType', {
    allowedValues: QUERY_TYPES
  })) !== null && _params$get !== void 0 ? _params$get : DEFAULT_QUERY_TYPE;
  const sort = params.get('sort', {
    allowedValues: SORT_OPTIONS
  });

  // Use ElasticSearch when cache exists and user applies filters or custom sort
  const shouldUseElasticSearch = useMemo(() => {
    if (!shouldUseCachedSearchData) return false;
    if (appliedFilters.length > 0) return true;
    if (sort && sort !== 'RELEVANCE') return true;
    return false;
  }, [appliedFilters.length, shouldUseCachedSearchData, sort]);

  // Transform search parameters based on current state
  const searchParams = useSearchQueryTransformer({
    appliedFilters,
    cachedListingIds,
    page,
    queryType,
    shouldUseElasticSearch,
    sort
  });

  // Execute the search query with appropriate parameters
  const searchResult = usePersonalizationQuery({
    app: SEARCH_QUERY,
    options: {
      context: {
        timeout: SEARCH_TIMEOUT_MS
      },
      skip: collectionResult.loading || !!collectionResult.error,
      variables: Object.assign({
        collectionId: (_collectionResult$dat = collectionResult.data) === null || _collectionResult$dat === void 0 ? void 0 : _collectionResult$dat.id,
        queryType: shouldUseElasticSearch ? 'ELASTIC_SEARCH' : queryType,
        search: shouldUseElasticSearch ? undefined : params.get('search')
      }, searchParams)
    },
    public: PUBLIC_SEARCH_QUERY
  });

  // Process and cache search results when they change
  useEffect(() => {
    var _searchResult$data;
    if ((_searchResult$data = searchResult.data) !== null && _searchResult$data !== void 0 && _searchResult$data.result) {
      processSearchResultData(searchResult.data.result);
    }
  }, [processSearchResultData, searchResult]);
  const processedResult = getProcessedSearchData((_searchResult$data2 = searchResult.data) === null || _searchResult$data2 === void 0 ? void 0 : _searchResult$data2.result);
  return {
    data: processedResult ? {
      result: processedResult
    } : undefined,
    error: collectionResult.error || searchResult.error,
    loading: collectionResult.loading || searchResult.loading
  };
}